import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/auth";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LogOut, LayoutDashboard, Home, PlusCircle, Download } from "lucide-react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

export function UserMenu() {
    const { user, logout } = useAuth();
    const [location, navigate] = useLocation();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    if (!user) return null;

    // Get the initials from the username
    const initials = user.username
        .split(/[._-]/)
        .map(part => part[0])
        .join('')
        .toUpperCase();

    const isOnDashboard = location === "/dashboard";
    const isOnSubmit = location === "/submit";

    const handleNavigation = (path: string) => {
        // Invalidate relevant queries based on the destination
        if (path === "/") {
            queryClient.invalidateQueries({ queryKey: ["/api/ideas"] });
        } else if (path === "/dashboard") {
            queryClient.invalidateQueries({ queryKey: ["/api/admin/ideas"] });
        }
        navigate(path);
    };

    const handleDownload = async () => {
        try {
          // Generate timestamp on the client-side
          const now = new Date();
          const year = now.getFullYear();
          const month = (now.getMonth() + 1).toString().padStart(2, '0');
          const day = now.getDate().toString().padStart(2, '0');
          const hours = now.getHours().toString().padStart(2, '0');
          const minutes = now.getMinutes().toString().padStart(2, '0');
          const seconds = now.getSeconds().toString().padStart(2, '0');
          const clientTimestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;

          const res = await fetch(`/api/admin/ideas/download?clientTimestamp=${clientTimestamp}`);
          if (!res.ok) {
            const errorData = await res.json().catch(() => ({ message: "Failed to download ideas report" }));
            throw new Error(errorData.message || "Failed to download ideas report");
          }

          // Get filename from Content-Disposition header
          const disposition = res.headers.get('Content-Disposition');
          let filename = 'ideas.xlsx'; // Default filename
          if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, '');
            }
          }

          // Trigger file download
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename; // Use filename from header or default
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
    
          toast({
            title: "Success",
            description: "Ideas report downloaded successfully.",
          });
        } catch (error) {
          toast({
            variant: "destructive",
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to download report",
          });
        }
      };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-3 px-4 py-2 rounded-full cursor-pointer hover:bg-blue-50/80 hover:shadow-md transition-all">
                    <span className="text-sm font-medium">{user.username}</span>
                    <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                            {initials}
                        </AvatarFallback>
                    </Avatar>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
                {(isOnSubmit || isOnDashboard) && (
                    <DropdownMenuItem
                        onClick={() => handleNavigation("/")}
                        className="cursor-pointer"
                    >
                        <Home className="mr-2 h-4 w-4" />
                        Home
                    </DropdownMenuItem>
                )}
                {user.isAdmin && !isOnDashboard && (
                    <DropdownMenuItem
                        onClick={() => handleNavigation("/dashboard")}
                        className="cursor-pointer"
                    >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                    </DropdownMenuItem>
                )}
                {!isOnSubmit && (
                    <DropdownMenuItem
                        onClick={() => handleNavigation("/submit")}
                        className="cursor-pointer"
                    >
                        <PlusCircle className="mr-2 h-4 w-4" />
                        New Idea
                    </DropdownMenuItem>
                )}
                {user.isAdmin && isOnDashboard && (
                    <DropdownMenuItem
                        onClick={handleDownload}
                        className="cursor-pointer"
                    >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                    </DropdownMenuItem>
                )}
                {((isOnSubmit || isOnDashboard) || user.isAdmin || !isOnSubmit) && <DropdownMenuSeparator />}
                <DropdownMenuItem
                    onClick={logout}
                    className="text-red-600 hover:text-red-700 focus:text-red-700 cursor-pointer"
                >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
} 